import { FlexRow, type FlexProps } from '@/components/ui/flex';
import { cn } from '@/utils/classUtils';
import {
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FC,
  type FormEvent,
  type KeyboardEvent,
} from 'react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { type HandleSubmitMessageType } from '@/utils/aiUtils';
import { ArrowRightIcon, Square } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CHAT_CONSTANTS } from '@/constants/chat';
import { useChatCtx } from './ChatContext';
import { RegionBadges } from './RegionBadges';
import { RegionSelector } from './RegionSelector';

export const Input: FC<
  Omit<FlexProps, 'onSubmit'> & {
    type: 'chat' | 'homepage';
    onSubmit: (message?: HandleSubmitMessageType) => void;
    onStop?: () => void;
    placeholder?: string;
    disabled?: boolean;
    loading?: boolean;
    hideRegionSelector?: boolean;
  }
> = ({
  type,
  disabled,
  onSubmit,
  onStop,
  placeholder,
  loading,
  hideRegionSelector,
}) => {
  const {
    region,
    setRegion,
    regionInferenceRequired,
    setRegionInferenceRequired,
  } = useChatCtx();
  const [input, setInput] = useState('');

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const tooltipMessage = useMemo(() => {
    if (disabled) {
      return 'Chat is disabled';
    }
    if (!input.trim().length) {
      return 'Type a message to send';
    }
    return 'Send message';
  }, [disabled, input]);

  const showPlaceholder = !input.length;

  const isDisabled =
    disabled || !input.trim().length || (regionInferenceRequired && !region);

  const adjustTextareaHeight = () => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto';
      const newHeight = Math.min(
        textAreaRef.current.scrollHeight,
        CHAT_CONSTANTS.TEXTAREA.MAX_HEIGHT
      );
      textAreaRef.current.style.height = `${newHeight}px`;
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isDisabled) {
      return;
    }

    onSubmit({
      text: input,
      metadata: { region: region ?? 'WORLD' },
    });
    setInput('');
    adjustTextareaHeight();
  };

  const handleKeyDown = async (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isDisabled) {
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      onSubmit({
        text: input,
        metadata: { region: region ?? 'WORLD' },
      });
      setInput('');
      adjustTextareaHeight();
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    adjustTextareaHeight();
  };

  if (!input.length) {
    adjustTextareaHeight();
  }

  const width =
    type === 'homepage' ? 'w-full' : 'w-full max-w-[950px] px-10 pb-4';

  return (
    <div className={width}>
      {regionInferenceRequired && type === 'chat' && (
        <RegionBadges
          onSelect={selectedRegion => {
            setRegion(selectedRegion);
            setRegionInferenceRequired(false);
          }}
          onSendMessage={(message: HandleSubmitMessageType) => {
            onSubmit(message);
          }}
        />
      )}
      <form onSubmit={handleSubmit}>
        <div
          className={cn(
            'w-full',
            'flex flex-col items-center',
            'p-4',
            'space-x-2',
            'border border-neutral-300 rounded-lg'
          )}
        >
          <div className="w-full">
            <Textarea
              className={cn(
                'w-auto flex-grow',
                'border-none resize-none shadow-none focus:ring-0 p-0 focus-visible:ring-0',
                'text-base text-primary',
                'placeholder:text-base placeholder:text-tertiary'
              )}
              placeholder={
                showPlaceholder
                  ? (placeholder ?? 'Ask a regulatory affairs question...')
                  : undefined
              }
              ref={textAreaRef}
              onKeyDown={handleKeyDown}
              onInput={handleInputChange}
              value={input}
              style={{
                maxHeight: `${CHAT_CONSTANTS.TEXTAREA.MAX_HEIGHT}px`,
                overflowY: 'auto',
              }}
            />
          </div>
          <FlexRow className="w-full justify-between items-center">
            {type === 'chat' && !hideRegionSelector ? (
              <div className="flex-shrink-0">
                <RegionSelector
                  value={region}
                  onValueChange={value => {
                    setRegion(value);
                    setRegionInferenceRequired(false);
                  }}
                  disabled={disabled || loading}
                />
              </div>
            ) : (
              <div />
            )}
            <SubmitAction
              onStop={onStop}
              isDisabled={isDisabled}
              loading={loading}
              tooltipMessage={tooltipMessage}
            />
          </FlexRow>
        </div>
      </form>
    </div>
  );
};

export const SubmitAction: FC<
  Omit<FlexProps, 'onSubmit'> & {
    onStop?: () => void;
    isDisabled?: boolean;
    loading?: boolean;
    tooltipMessage?: string;
  }
> = ({ isDisabled, onStop, loading, tooltipMessage }) => {
  return (
    <div className="flex-grow-0 flex-shrink w-fit">
      {onStop ? (
        <Button
          size="icon"
          variant="outline"
          className="rounded-[120px] min-h-[36px] h-[36px] min-w-[36px] w-[36px]"
          onClick={onStop}
          type="button"
        >
          <Square className="w-4 h-4 fill-neutral-700" />
        </Button>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Button
                className={cn(
                  'rounded-[120px] min-h-[36px] h-[36px] min-w-[36px] w-[36px]',
                  !isDisabled && 'bg-yellow-400 text-neutral-700 shadow-none'
                )}
                disabled={isDisabled}
                loading={loading}
                variant={isDisabled ? 'outline' : 'default'}
                size="sm"
                type="submit"
              >
                <ArrowRightIcon />
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>{tooltipMessage}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};
